import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { VatRateRestDataProvider } from '../../../data/VatRateRestDataProvider';
import { VatRate } from '../../../models/VatRate';
import './VatRatesTab.scss';
import { useTranslation } from 'react-i18next';

const VatRatesTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [vatRates, setVatRates] = useState<VatRate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const provider = React.useMemo(() => new VatRateRestDataProvider(), []);

    const columns: DetailedListColumn<VatRate>[] = React.useMemo(() => [
        { key: 'name',       header: t('screens:vatRates.columns.name'),       dataField: 'name',       type: 'string',   required: true },
        { key: 'percentage', header: t('screens:vatRates.columns.percentage'),  dataField: 'percentage', type: 'number',   required: true },
        { key: 'isMoved',    header: t('screens:vatRates.columns.moved'),       dataField: 'isMoved',    type: 'checkbox',
          render: (r: VatRate) => r.isMoved ? t('common:yes') : t('common:no') },
        { key: 'isExempt',   header: t('screens:vatRates.columns.exempt'),      dataField: 'isExempt',   type: 'checkbox',
          render: (r: VatRate) => r.isExempt ? t('common:yes') : t('common:no') },
        { key: 'isActive',   header: t('screens:vatRates.columns.active'),      dataField: 'isActive',   type: 'checkbox',
          render: (r: VatRate) => r.isActive ? t('common:yes') : t('common:no') },
    ], [t]);

    useEffect(() => {
        setLoading(true);
        provider.getAll()
            .then(setVatRates)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [provider]);

    const handleChange = async (newRates: VatRate[], action: 'add' | 'edit' | 'delete', changed: VatRate) => {
        try {
            if (action === 'add') {
                await provider.add(changed);
            } else if (action === 'edit') {
                await provider.update(changed.id, changed);
            } else if (action === 'delete') {
                await provider.delete(changed.id);
            }

            const refreshed = await provider.getAll();
            setVatRates(refreshed);
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div className="vat-rates-tab">
            <SubsectionPanel title={t('screens:vatRates.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <InfoPanel header="About VAT rates">
                    These are the VAT rates available for use on invoices. Rates marked as <strong>Moved</strong> shift the VAT liability to the customer. Rates marked as <strong>Exempt</strong> carry no VAT obligation. Only <strong>Active</strong> rates can be selected when creating invoices.
                </InfoPanel>
                <DetailedListComponent
                    dataSource={{ data: vatRates, setData: setVatRates }}
                    columns={columns}
                    rowKey={r => r.id}
                    onChange={handleChange}
                />
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>
        </div>
    );
};

export default VatRatesTab;
